/* jw-email-guard.js — John White Shoes
 *
 * Stops mistyped email domains becoming dead signups.
 *
 * Why this exists: an audit on 9 Sep 2026 found 73 Shopify subscribers absent
 * from Mailchimp. Almost all were typo domains — gamil.com, hotnail.com,
 * gmail.comm, gnail.com, gmail.vom. Shopify accepts them because type="email"
 * only checks the shape. Mailchimp rejects them as undeliverable, so the
 * person is counted as a subscriber and never receives anything.
 *
 * Behaviour is deliberately soft. A suspected typo shows an inline suggestion
 * and holds the first submit. If the visitor submits again unchanged it goes
 * through. Nobody with an unusual but real domain is ever locked out.
 *
 * No dependencies. Attaches to every Shopify customer form on the page.
 */
(function () {
  'use strict';

  var KNOWN = [
    'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.co.uk',
    'outlook.com', 'outlook.co.uk', 'live.co.uk', 'live.com', 'msn.com',
    'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'icloud.com', 'me.com', 'mac.com',
    'aol.com', 'btinternet.com', 'sky.com', 'virginmedia.com', 'talktalk.net',
    'ntlworld.com', 'blueyonder.co.uk', 'tiscali.co.uk', 'protonmail.com',
    'proton.me', 'gmx.com', 'gmx.co.uk', 'comcast.net', 'verizon.net',
    'yahoo.ca', 'hotmail.fr', 'orange.fr', 'wanadoo.fr', 'web.de', 't-online.de'
  ];

  /* Domains seen in the real JWS data. Exact matches, no guessing. */
  var TYPOS = {
    'gamil.com': 'gmail.com', 'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com',
    'gmail.co': 'gmail.com', 'gmail.con': 'gmail.com', 'gmail.vom': 'gmail.com',
    'gmail.comm': 'gmail.com', 'gmail.cm': 'gmail.com', 'gmaik.com': 'gmail.com',
    'gnail.com': 'gmail.com', 'gmaill.com': 'gmail.com', 'gmail.om': 'gmail.com',
    'hotnail.com': 'hotmail.com', 'hotmial.com': 'hotmail.com',
    'hotmail.om': 'hotmail.com', 'hotmail.co': 'hotmail.com',
    'hotmail.con': 'hotmail.com', 'hitmail.com': 'hotmail.com',
    'hmail.com': 'hotmail.com', 'hotmai.com': 'hotmail.com',
    'gmil.com': 'gmail.com', 'gmail.c': 'gmail.com', 'ail.com': 'gmail.com',
    'gmail.ccom': 'gmail.com', 'gmali.com': 'gmail.com', 'gmail.uk': 'gmail.com',
    'homail.co.uk': 'hotmail.co.uk', 'hotmail.couk': 'hotmail.co.uk',
    'hotmail.c': 'hotmail.com', 'hotmail.comm': 'hotmail.com',
    'yahoo.co': 'yahoo.co.uk', 'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com',
    'yahoo.com.': 'yahoo.com', 'yahoo.c': 'yahoo.com', 'yhoo.com': 'yahoo.com',
    'icloud.comm': 'icloud.com', 'iclod.com': 'icloud.com',
    'outlook.comm': 'outlook.com', 'outloo.com': 'outlook.com',
    'btinternet.comm': 'btinternet.com', 'btinernet.com': 'btinternet.com',
    'live.couk': 'live.co.uk', 'live.co.uk.': 'live.co.uk',
    'icloud.co': 'icloud.com', 'iclould.com': 'icloud.com', 'icloud.con': 'icloud.com',
    'outlook.co': 'outlook.com', 'outlok.com': 'outlook.com',
    'btintenet.com': 'btinternet.com', 'btinternet.co': 'btinternet.com',
    'live.co': 'live.co.uk', 'googlemail.co': 'googlemail.com'
  };

  function distance(a, b) {
    var m = a.length, n = b.length, prev = [], cur = [], i, j;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      cur[0] = i;
      for (j = 1; j <= n; j++) {
        cur[j] = Math.min(
          prev[j] + 1,
          cur[j - 1] + 1,
          prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1)
        );
      }
      for (j = 0; j <= n; j++) prev[j] = cur[j];
    }
    return prev[n];
  }

  /* Returns a suggested domain, or null when the address looks fine. */
  function suggest(email) {
    var at = email.lastIndexOf('@');
    if (at < 1) return null;
    var domain = email.slice(at + 1).toLowerCase().trim();
    if (!domain) return null;
    if (KNOWN.indexOf(domain) !== -1) return null;
    if (TYPOS[domain]) return TYPOS[domain];

    /* No dot, or a TLD too short to be real. */
    var dot = domain.lastIndexOf('.');
    if (dot < 1 || domain.length - dot < 3) {
      var stem = dot > 0 ? domain.slice(0, dot) : domain;
      for (var k = 0; k < KNOWN.length; k++) {
        if (KNOWN[k].indexOf(stem + '.') === 0) return KNOWN[k];
      }
      return null;
    }

    /* One edit away from a common domain, with the same opening letters.
       The prefix check keeps real domains like gmail.de from being nagged. */
    for (var i = 0; i < KNOWN.length; i++) {
      var cand = KNOWN[i];
      if (cand.slice(0, 3) !== domain.slice(0, 3)) continue;
      if (distance(domain, cand) === 1) return cand;
    }
    return null;
  }

  /* The hint is appended after the FORM, never next to the input. The JW
     newsletter form is a flex row, so a sibling inserted beside the input
     becomes a flex child and collapses the field to a sliver. */
  function hint(form) {
    if (form.__jwEg) return form.__jwEg;
    var el = document.createElement('div');
    el.className = 'jw-eg';
    el.setAttribute('role', 'status');
    el.style.cssText =
      'display:none;margin:.55rem 0 0;font-size:.82rem;line-height:1.45;' +
      'color:#C4A96C;font-family:inherit;text-transform:none;' +
      'letter-spacing:normal;font-style:normal;text-align:left;';
    form.parentNode.insertBefore(el, form.nextSibling);
    form.__jwEg = el;
    return el;
  }

  function clear(form, input) {
    if (form.__jwEg) form.__jwEg.style.display = 'none';
    input.removeAttribute('data-jw-eg-warned');
  }

  function show(form, input, fixed) {
    var el = hint(form);
    el.innerHTML = '';
    el.appendChild(document.createTextNode('Did you mean '));
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = fixed;
    /* The section CSS styles `.jw-nl__form button` as an uppercase, letter-spaced
       CTA, so every inherited property is overridden explicitly. Inline wins. */
    btn.style.cssText =
      'background:none;border:0;padding:0;margin:0;font:inherit;color:inherit;' +
      'text-transform:none;letter-spacing:normal;font-size:inherit;' +
      'font-weight:inherit;line-height:inherit;white-space:normal;' +
      'text-decoration:underline;cursor:pointer;';
    btn.addEventListener('click', function () {
      input.value = fixed;
      clear(form, input);
      input.focus();
    });
    el.appendChild(btn);
    el.appendChild(document.createTextNode('?'));
    el.style.display = 'block';
  }

  function check(form, input) {
    var v = (input.value || '').trim();
    if (!v) { clear(form, input); return null; }
    var at = v.lastIndexOf('@');
    if (at < 1) { clear(form, input); return null; }
    var sg = suggest(v);
    if (!sg) { clear(form, input); return null; }
    show(form, input, v.slice(0, at + 1) + sg);
    return sg;
  }

  function attach(form) {
    if (form.getAttribute('data-jw-eg')) return;
    var input = form.querySelector('input[type="email"], input[name="contact[email]"]');
    if (!input) return;
    form.setAttribute('data-jw-eg', '1');

    input.addEventListener('blur', function () { check(form, input); });
    input.addEventListener('input', function () {
      if (form.__jwEg && form.__jwEg.style.display === 'block') clear(form, input);
    });

    form.addEventListener('submit', function (e) {
      /* Hold the first submit only. A second submit always goes through, so an
         unusual but real domain is never locked out. */
      if (input.getAttribute('data-jw-eg-warned')) return;
      if (check(form, input)) {
        input.setAttribute('data-jw-eg-warned', '1');
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  function scan() {
    var forms = document.querySelectorAll('form[action*="/contact"], form[action*="/account"]');
    for (var i = 0; i < forms.length; i++) attach(forms[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  /* The Inner Circle popup is injected after load, so re-scan on changes. */
  if (window.MutationObserver) {
    new MutationObserver(scan).observe(document.documentElement, {
      childList: true, subtree: true
    });
  }
})();
